// Port of SuiteSync/Review.swift's values: `ReviewCopy` (and `make`), `ReviewNote`, and `ReaderProgress`
// (SuiteModel/ReaderRound.swift) as a reader sends it. A copy is the manuscript as it stood at a checkpoint, prose only
// (no notes or research, nothing live), with each section's stored text: a reader's anchors are Unicode-scalar offsets
// into it. Web links add two optional fields the Swift's decoder ignores: `reader` (the name the writer gave this link's
// reader) and `notesBy` (the round's notes-by date).
import type * as A from '@automerge/automerge';
import { tree as buildTree, type ArrangementTreeNode } from '../model/arrangement.ts';
import { readProject, type CheckpointRecord } from '../model/documents.ts';
import { arrangementNamed, checkpointState } from '../history/projectDiff.ts';
import type { ReviewCopy, ReviewCopySection } from './values.ts';
export * from './values.ts';

/** `ReviewError.checkpointIncomplete` and friends. */
export class ReviewError extends Error {
  constructor(
    readonly code: 'checkpointIncomplete' | 'noCheckpoint' | 'noManuscript',
    message: string,
    readonly missing = 0,
  ) {
    super(message);
  }
}

const FRAGMENT = 'fragment.';

/**
 * `ReviewCopy.make`: the manuscript arrangement at `checkpoint`, in order. Refuses when fragments the checkpoint names
 * haven't reached this computer (`StateResolver.at`'s `missingFragments`), never guessing at them. Notes and research
 * (by the fragment's kind now, as the Swift reads it) are left out with everything under them.
 */
export function makeReviewCopy(
  docs: Map<string, A.Doc<unknown>>,
  checkpoint: CheckpointRecord,
  o: { from: string; message: string; id: string; now?: Date; reader?: string; notesBy?: Date | null },
): ReviewCopy {
  const state = checkpointState(docs, checkpoint);
  if (state.missingFragments.length > 0) {
    const n = state.missingFragments.length;
    throw new ReviewError('checkpointIncomplete', `${n} fragment(s) of this checkpoint haven't synced to this device yet. Try again shortly.`, n);
  }
  const project = readProject(docs.get('project')!);
  const kinds = new Map([...project.fragments].map(([id, e]) => [id, e.kind]));
  const sections: ReviewCopySection[] = [];
  const walk = (nodes: ArrangementTreeNode[], depth: number) => {
    for (const n of nodes) {
      const fid = n.node.fragmentID;
      const kind = fid !== undefined ? kinds.get(fid) : undefined;
      if (kind === 'note' || kind === 'research') continue;
      const f = fid !== undefined ? state.fragments.get(fid) : undefined;
      if (fid !== undefined && f) {
        sections.push({ fragmentID: fid, title: f.title, text: f.text, depth, isFolder: n.node.isFolder || n.children.length > 0 });
      }
      walk(n.children, depth + 1);
    }
  };
  const manuscript = arrangementNamed(state);
  walk(manuscript ? buildTree(manuscript) : [], 0);
  return {
    id: o.id,
    projectID: project.id,
    projectTitle: project.title,
    checkpointID: checkpoint.id,
    checkpointName: checkpoint.name,
    from: o.from,
    message: o.message,
    createdAt: o.now ?? new Date(),
    sections,
    ...(o.reader ? { reader: o.reader } : {}),
    ...(o.notesBy ? { notesBy: o.notesBy } : {}),
  };
}

/** Whether a document name is a fragment's. */
export const isFragmentDoc = (name: string) => name.startsWith(FRAGMENT);

